import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/lib/seo";
import Reveal from "@/components/Reveal";
import { formatDate } from "@/lib/format";

function renderContent(content) {
  if (!content) return null;
  return content.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={`h-${i}-${block.slice(3, 30)}`} className="font-serif text-2xl md:text-3xl text-teal mt-10 mb-4">
          {block.replace("## ", "")}
        </h2>
      );
    }
    return (
      <p key={`p-${i}-${block.slice(0, 30)}`} className="text-ink/85 leading-relaxed mb-5">
        {block}
      </p>
    );
  });
}

export default function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-5 px-6 text-center pt-24">
        <p className="font-serif text-4xl text-teal-dark">Article introuvable</p>
        <Link data-testid="article-notfound-back" to="/conseils" className="rounded-full bg-teal px-8 py-3 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
          Retour aux conseils
        </Link>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-[70vh] pt-36 max-w-4xl mx-auto px-6"><div className="h-96 rounded-3xl bg-cream-light animate-pulse" /></div>;
  }

  return (
    <div data-testid="article-page">
      <SEO title={post.title} description={post.excerpt} image={post.cover} type="article" />
      <article className="pt-32 md:pt-40 pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link data-testid="article-back-link" to="/conseils" className="inline-flex items-center gap-1.5 text-sm text-mauve hover:text-teal transition-colors mb-8">
            <ChevronLeft size={16} /> Retour aux conseils
          </Link>
          <Reveal>
            <p className="eyebrow mb-4">{formatDate(post.created_at)}{post.tags?.length ? ` • ${post.tags.join(" • ")}` : ""}</p>
            <h1 className="font-serif text-3xl md:text-6xl text-teal-dark leading-tight">{post.title}</h1>
          </Reveal>
          {post.cover && (
            <Reveal delay={0.1}>
              <div className="mt-10 overflow-hidden rounded-[2rem] shadow-[0_24px_70px_rgba(16,114,105,0.15)]">
                <img src={post.cover} alt={post.title} className="w-full h-72 md:h-[440px] object-cover" />
              </div>
            </Reveal>
          )}
          <div className="mt-10 text-lg">{renderContent(post.content)}</div>
          <div className="mt-14 rounded-3xl bg-mint/20 border border-mint/40 p-8 text-center">
            <p className="font-serif text-2xl text-teal-dark">Envie de passer à l'action ?</p>
            <p className="mt-2 text-ink/70 text-sm">Découvrez nos essentiels d'allaitement, livrés chez vous à Dakar sous 24h.</p>
            <Link data-testid="article-cta-boutique" to="/boutique" className="mt-5 inline-block rounded-full bg-teal px-8 py-3 text-sm font-medium text-cream hover:bg-teal-dark transition-colors">
              Visiter la boutique
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
